import { createApiClient, getRuntimeConfig, useAuth, type ApiClient } from '@berky/auth'
import { useMemo } from 'react'

export type ExternalRecipientPolicyMode = 'Disabled' | 'AllowedDomains' | 'Unrestricted'

export type NotificationJobStatus =
  'Queued' | 'Processing' | 'RetryScheduled' | 'Accepted' | 'Failed' | 'Suppressed'

export interface NotificationStatus {
  isConfigured: boolean
  externalDeliveryEnabled: boolean
  externalRecipientMode: ExternalRecipientPolicyMode
  allowedExternalDomains: string[]
  externalRecipientConfigurationIssue: string | null
  hasStoredExternalRecipientPolicy: boolean
  attachmentStorageConfigured: boolean
  isPaused: boolean
  channelStatus: 'NotConfigured' | 'Available' | 'Unavailable'
  senderAddress: string
  senderDisplayName: string
  lastAcceptedAt: string | null
  queuedCount: number
  failedCount: number
  oldestQueuedAt: string | null
  openEscalationCount: number
  affectedModules: string[]
}

export interface ExternalRecipientPolicyCheck {
  isSyntacticallyValid: boolean
  isAllowed: boolean
  domain: string | null
  mode: ExternalRecipientPolicyMode
  reason: string | null
}

export interface ExternalRecipientPolicyUpdate {
  mode: ExternalRecipientPolicyMode
  allowedDomains: string[]
}

export interface NotificationHistoryItem {
  id: string
  updatedAt: string
  originKind: 'Module' | 'Platform'
  originId: string
  typeKey: string
  recipientKind: 'Internal' | 'ExternalEmail'
  recipient: string
  status: NotificationJobStatus
  attemptCount: number
  lastError: string | null
}

export interface NotificationHistoryPage {
  items: NotificationHistoryItem[]
  page: number
  pageSize: number
  total: number
}

export interface FailedNotification {
  id: string
  originKind: 'Module' | 'Platform'
  originId: string
  typeKey: string
  recipient: string
  updatedAt: string
  attemptCount: number
  lastError: string | null
}

export interface RegisteredNotificationType {
  key: string
  originKind: 'Module' | 'Platform'
  originId: string
  name: string
  description: string
  defaultEnabled: boolean
  isMandatory: boolean
  allowsExternalRecipients: boolean
  allowsAttachments: boolean
  isPaused: boolean
}

export type HomepageNewsTone = 'Information' | 'Important' | 'Urgent'
export type HomepageNewsBlockType = 'paragraph' | 'heading' | 'bulletList' | 'highlight'

export interface HomepageNewsBlock {
  type: HomepageNewsBlockType
  text: string | null
  items: string[]
}

export interface HomepageNewsDraft {
  eyebrow: string
  title: string
  tone: HomepageNewsTone
  blocks: HomepageNewsBlock[]
  actionLabel: string | null
  actionUrl: string | null
  validFrom: string
  validUntil: string
}

export interface HomepageNews extends HomepageNewsDraft {
  id: string
  isCancelled: boolean
  createdByUserId: string
  createdAt: string
  updatedByUserId: string
  updatedAt: string
}

export interface HomepageNewsUserStatistic {
  userId: string
  name: string
  email: string | null
  presentationCount: number
  lastPresentedAt: string
}

export interface HomepageNewsStatisticsPage {
  newsId: string
  reachedUsers: number
  totalPresentations: number
  lastPresentedAt: string | null
  items: HomepageNewsUserStatistic[]
  page: number
  pageSize: number
}

export function useNotificationsApi(): ApiClient {
  const { getAccessToken, reportApiAuthError } = useAuth()
  const { platformScope } = getRuntimeConfig()
  const scopes = useMemo(() => (platformScope ? [platformScope] : []), [platformScope])

  return useMemo(
    () =>
      createApiClient({
        baseUrl: '/api/platform/notifications',
        scopes,
        getAccessToken,
        onAuthError: reportApiAuthError,
      }),
    [scopes, getAccessToken, reportApiAuthError],
  )
}
